import {
  ApplyResults,
  PipelineStatusResponse,
  PrepareResults,
  ProcessResults,
} from './types'
import {
  makeContentServiceDeleteRequest,
  makeContentServiceGetRequest,
  makeContentServicePostRequest,
} from 'infrastructure/contentService'

/**
 * Start the prepare stage of the SVG to LaTeX conversion pipeline.
 *
 * @param treeId - The tree ID
 * @param versionId - The tree version
 * @returns Promise<unknown> - The response data from the prepare stage
 */
export async function startPrepare(
  treeId: string,
  _versionId: string,
): Promise<unknown> {
  const response = await makeContentServicePostRequest(
    `content/v1/svg_to_latex/prepare_svg_jobs?tree_id=${treeId}:${_versionId}&provider=openai&model=gpt-5`,
    {},
  )

  try {
    const data = await response.json()
    return data
  } catch {
    const rawResponse = await response.text()
    throw new Error(rawResponse)
  }
}

/**
 * Get prepare results for the SVG to LaTeX conversion.
 *
 * @param treeId - The tree ID
 * @param versionId - The tree version
 * @returns Promise<PrepareResults> - The response data from the prepare results
 */
export async function getPrepareResults(
  treeId: string,
  _versionId: string,
): Promise<PrepareResults> {
  const response = await makeContentServiceGetRequest(
    `content/v1/svg_to_latex/prepare/flow_results/${treeId}`,
  )

  try {
    const data = await response.json()
    return data
  } catch {
    const rawResponse = await response.text()
    throw new Error(rawResponse)
  }
}

/**
 * Start the process stage of the SVG to LaTeX conversion pipeline.
 *
 * @param treeId - The tree ID
 * @param versionId - The tree version
 * @param step1ControllerId - The controller ID from step 1
 * @param provider - The AI provider (openai or gemini)
 * @param model - The model to use
 * @returns Promise<unknown> - The response data from the process stage
 */
export async function startProcess(
  treeId: string,
  versionId: string,
  step1ControllerId: string,
  provider: string,
  model: string,
): Promise<unknown> {
  const response = await makeContentServicePostRequest(
    `content/v1/svg_to_latex/process_svgs?tree_id=${treeId}:${versionId}&step1_controller_id=${step1ControllerId}&provider=${provider}&model=${model}`,
    {},
  )

  try {
    const data = await response.json()
    return data
  } catch {
    const rawResponse = await response.text()
    throw new Error(rawResponse)
  }
}

/**
 * Get process results for the SVG to LaTeX conversion.
 *
 * @param treeId - The tree ID
 * @param versionId - The tree version
 * @returns Promise<ProcessResults> - The response data from the process results
 */
export async function getProcessResults(
  treeId: string,
  _versionId: string,
): Promise<ProcessResults> {
  const response = await makeContentServiceGetRequest(
    `content/v1/svg_to_latex/process/flow_results/${treeId}`,
  )

  try {
    const data = await response.json()
    return data
  } catch {
    const rawResponse = await response.text()
    throw new Error(rawResponse)
  }
}

/**
 * Start the apply stage of the SVG to LaTeX conversion pipeline.
 *
 * @param treeId - The tree ID
 * @param versionId - The tree version
 * @param step2ControllerId - The controller ID from step 2
 * @returns Promise<unknown> - The response data from the apply stage
 */
export async function startApply(
  treeId: string,
  versionId: string,
  step2ControllerId: string,
): Promise<unknown> {
  const response = await makeContentServicePostRequest(
    `content/v1/svg_to_latex/update_tree?tree_id=${treeId}:${versionId}&step2_controller_id=${step2ControllerId}`,
    {},
  )

  try {
    const data = await response.json()
    return data
  } catch {
    const rawResponse = await response.text()
    throw new Error(rawResponse)
  }
}

/**
 * Get apply results for the SVG to LaTeX conversion.
 *
 * @param treeId - The tree ID
 * @param versionId - The tree version
 * @returns Promise<ApplyResults> - The response data from the apply results
 */
export async function getApplyResults(
  treeId: string,
  _versionId: string,
): Promise<ApplyResults> {
  const response = await makeContentServiceGetRequest(
    `content/v1/svg_to_latex/update/flow_results/${treeId}`,
  )

  try {
    const data = await response.json()
    return data
  } catch {
    const rawResponse = await response.text()
    throw new Error(rawResponse)
  }
}

/**
 * Get the pipeline status for the SVG to LaTeX conversion.
 *
 * @param treeId - The tree ID
 * @param versionId - The tree version
 * @returns Promise<PipelineStatusResponse> - The response data from the pipeline status
 */
export async function getPipelineStatus(
  treeId: string,
  _versionId: string,
): Promise<PipelineStatusResponse> {
  const response = await makeContentServiceGetRequest(
    `content/v1/svg_to_latex/pipeline/status/${treeId}:${_versionId}`,
  )

  try {
    const data = await response.json()
    return data
  } catch {
    const rawResponse = await response.text()
    throw new Error(rawResponse)
  }
}

/**
 * Remove the SVG to LaTeX conversion pipeline.
 *
 * @param treeId - The tree ID
 * @param versionId - The tree version
 * @returns Promise<unknown> - The response data from the remove operation
 */
export async function removePipeline(
  treeId: string,
  _versionId: string,
): Promise<unknown> {
  const response = await makeContentServiceDeleteRequest(
    `content/v1/svg_to_latex/pipeline/delete/${treeId}:${_versionId}`,
  )

  try {
    const data = await response.json()
    return data
  } catch {
    const rawResponse = await response.text()
    throw new Error(rawResponse)
  }
}
